// @flow

import React, { Component, Fragment, type Element } from "react";
import { noop, has } from "lodash";
import type { BinaryFn } from "@tvg/types/Functional";
import type { Device } from "@tvg/types/Device";
import type {
  PurchasedProductItem,
  PurchasedProductGroup,
  PopularProduct,
  ProductListItem
} from "@tvg/types/HandicappingStore";
import Icon from "../../_static/Icons";
import TitleHeader from "../../_molecule/TitleHeader";
import MessageBox from "../../_molecule/MessageBox";
import { arrowRight, pdf } from "../../_static/Icons/icons";

import buildColor from "../../_static/ColorPalette";
import {
  ArrowIcon,
  HandicappingStoreItem,
  HandicappingItemLink,
  IntTrackContainer,
  IntTracksButton,
  ProductTitle,
  ProductDescription,
  ProductList,
  SeeAllButton,
  ProcessingContainer,
  TracksNamesList,
  HandicappingItemWrapper,
  ExpandableHeaderWrapper,
  ExpandableProduct,
  SeeCardButton
} from "./styled-components";
import ProcessingOverlay from "../../_static/ProcessingOverlay";

type Props = {
  isLoading: boolean,
  popularProducts: Array<PopularProduct>,
  purchasedProducts: Array<PurchasedProductGroup | PurchasedProductItem>,
  onProductCallback: BinaryFn<string, ?string, void>,
  handicapStoreUrl: string,
  isAllowedTrack: boolean,
  handicappingStorePurchases: boolean,
  device: Device
};

export const renderTracksList = (productList: ProductListItem[]): string => {
  const resArr = [];
  productList.forEach((productItem) => {
    const trackName = productItem.track.split(" - ")[0];
    if (!resArr.includes(trackName)) resArr.push(trackName);
  });
  return resArr.join(", ");
};

export default class HandicappingStorePP extends Component<Props> {
  static defaultProps = {
    popularProducts: [],
    purchasedProducts: [],
    onProductCallback: noop,
    handicapStoreUrl: "#handicap-store",
    isAllowedTrack: true,
    isLoading: true,
    handicappingStorePurchases: false,
    device: "mobile"
  };

  expandableContent = (productsList: ProductListItem[]): Array<Element<*>> =>
    productsList.map(this.purchasedProduct);

  purchasedProduct = (product: PurchasedProductItem | ProductListItem) => (
    <HandicappingStoreItem
      borderTop={has(product, "track")}
      borderBlueColor
      key={product.viewProductLink}
      data-qa-label="purchased-product"
    >
      <HandicappingItemWrapper>
        <Icon icon={pdf} size={30} />
        <dl>
          <dt>
            <ProductTitle
              data-qa-label="popularProduct-title"
              dangerouslySetInnerHTML={{
                // $FlowFixMe
                __html: has(product, "name") ? product.name : product.track
              }}
            />
          </dt>
          {has(product, "startDate") && (
            <dd>
              <ProductDescription data-qa-label="popularProduct-description">
                {
                  // $FlowFixMe
                  product.trackName
                }{" "}
                -{" "}
                {
                  // $FlowFixMe
                  product.startDate
                }
              </ProductDescription>
            </dd>
          )}
        </dl>
        <SeeCardButton
          device={this.props.device}
          to={this.props.handicapStoreUrl}
          onClick={() => this.props.onProductCallback(product.viewProductLink)}
          data-qa-label="see-card-button"
        >
          <span>SEE CARD</span>
        </SeeCardButton>
      </HandicappingItemWrapper>
    </HandicappingStoreItem>
  );

  purchasedExpandableProduct = (product: PurchasedProductGroup) => (
    <ExpandableProduct
      key={product.productId}
      header={
        <ExpandableHeaderWrapper>
          <dl>
            <dt>
              <ProductTitle
                data-qa-label="popularProduct-title"
                dangerouslySetInnerHTML={{ __html: product.name }}
              />
            </dt>
            <dd>
              <TracksNamesList>
                {renderTracksList(product.productList)}
              </TracksNamesList>
              <ProductDescription data-qa-label="popularProduct-description">
                {product.startDate}
              </ProductDescription>
            </dd>
          </dl>
        </ExpandableHeaderWrapper>
      }
    >
      {this.expandableContent(product.productList)}
    </ExpandableProduct>
  );

  renderPopularProductsList = () =>
    this.props.popularProducts.map((product: PopularProduct) => (
      <HandicappingStoreItem key={product.link}>
        <HandicappingItemLink
          onClick={() =>
            this.props.onProductCallback(product.link, product.name)
          }
          to={this.props.handicapStoreUrl}
          data-qa-label="popularProductItem"
        >
          <dl>
            <dt>
              <ProductTitle data-qa-label="popularProduct-title">
                {product.name}
              </ProductTitle>
            </dt>
            <dd>
              <ProductDescription data-qa-label="popularProduct-description">
                {product.price}
              </ProductDescription>
            </dd>
          </dl>
          <ArrowIcon
            icon={arrowRight}
            color={buildColor("grey", "600")}
            qaLabel="arrow-right"
          />
        </HandicappingItemLink>
      </HandicappingStoreItem>
    ));

  renderPurchasedProductsList = () =>
    this.props.purchasedProducts.map(
      (product: PurchasedProductGroup | PurchasedProductItem) =>
        has(product, "productList")
          ? // $FlowFixMe
            this.purchasedExpandableProduct(product)
          : // $FlowFixMe
            this.purchasedProduct(product)
    );

  renderPopularProductsSection = (isAllowedTrack: boolean) =>
    isAllowedTrack && this.props.popularProducts.length ? (
      <Fragment>
        <TitleHeader
          title="popular products"
          showTopBorder
          qaLabel="handicapStore-popularproductTitle"
        />
        <ProductList data-qa-label="handicapStore-popularProductsList">
          {this.renderPopularProductsList()}
        </ProductList>
      </Fragment>
    ) : null;

  renderPurchasedProductsSection = (isAllowedTrack: boolean) =>
    isAllowedTrack && this.props.purchasedProducts.length ? (
      <Fragment>
        <TitleHeader
          title="purchased products"
          showTopBorder={false}
          qaLabel="handicapStore-purchasedproductTitle"
        />
        <ProductList data-qa-label="handicapStore-purchasedProductsList">
          {this.renderPurchasedProductsList()}
        </ProductList>
      </Fragment>
    ) : null;

  renderSeeAllButton = (isAllowedTrack: boolean) => {
    if (isAllowedTrack) {
      return (
        <SeeAllButton
          to={this.props.handicapStoreUrl}
          onClick={() => this.props.onProductCallback("")}
          data-qa-label="handicapStore-seeAllBtn"
        >
          see all products
          <ArrowIcon
            icon={arrowRight}
            color={buildColor("grey", "600")}
            qaLabel="arrow-right"
          />
        </SeeAllButton>
      );
    }
    return (
      <IntTrackContainer data-qa-label="handicapStore-emptyState">
        <MessageBox
          type="infoNoBackground"
          message="Handicapping store products are only available for US and CAN races."
          isVertical
          hasIcon
        />
        <IntTracksButton
          url={this.props.handicapStoreUrl}
          onClick={() => this.props.onProductCallback("")}
          type="secondary"
          qaLabel="handicapStore-emptyState-btn"
        >
          See PP&apos;s for US and CAN Races
        </IntTracksButton>
      </IntTrackContainer>
    );
  };

  render() {
    return !this.props.isLoading ? (
      <Fragment>
        {this.props.handicappingStorePurchases &&
          this.renderPurchasedProductsSection(this.props.isAllowedTrack)}
        {this.renderPopularProductsSection(this.props.isAllowedTrack)}
        {this.renderSeeAllButton(this.props.isAllowedTrack)}
      </Fragment>
    ) : (
      <ProcessingContainer>
        <ProcessingOverlay />
      </ProcessingContainer>
    );
  }
}
